// openEHR BASE model — BMM package org.openehr.base.base_types.identification
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { Any } from "../foundation_types/foundation_types.ts";
import { Boolean, String } from "../foundation_types/primitive_types.ts";

/**
 * Ancestor class of identifiers of informational objects. Ids may be completely meaningless, in which case their only job is to refer to something, or may carry some information to do with the identified object.
 *
 * Object ids are used inside an object to identify that object. To identify another object in another service, use an \`OBJECT_REF\`, or else use a UID for local objects identified by UID. If none of the subtypes is suitable, direct instances of this class may be used.
 */
export abstract class OBJECT_ID {
  /**
   * Internal storage for value
   * @protected
   */
  protected _value?: String;

  /**
   * The value of the id in the form defined below.
   */
  get value(): string | undefined {
    return this._value?.value;
  }

  /**
   * Gets the String wrapper object for value.
   * Use this to access String methods.
   */
  get $value(): String | undefined {
    return this._value;
  }

  /**
   * Sets value from either a primitive value or String wrapper.
   */
  set value(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._value = undefined;
    } else if (typeof val === "string") {
      this._value = String.from(val);
    } else {
      this._value = val;
    }
  }
}

/**
 * Identifier for archetypes. Ideally these would identify globally unique archetypes.
 *
 * Lexical form: \`rm_originator  '-' rm_name  '-' rm_entity  '.' concept_name {  '-' specialisation }*  '.v' number\`.
 */
export class ARCHETYPE_ID extends OBJECT_ID {
  /**
   * Globally qualified reference model entity, e.g.  \`"openehr-EHR-OBSERVATION"\`.
   * @returns Result value
   */
  qualified_rm_entity(): String {
    const val = this.value || "";
    const dotIndex = val.indexOf(".");
    if (dotIndex === -1) {
      throw new Error("Invalid ARCHETYPE_ID format: no '.' found");
    }
    return String.from(val.substring(0, dotIndex));
  }

  /**
   * Name of the concept represented by this archetype, including specialisation, e.g. \`"Biochemistry_result-cholesterol"\`.
   * @returns Result value
   */
  domain_concept(): String {
    const val = this.value || "";
    const dotIndex = val.indexOf(".");
    const vIndex = val.lastIndexOf(".v");

    if (dotIndex === -1 || vIndex === -1 || vIndex <= dotIndex) {
      throw new Error("Invalid ARCHETYPE_ID format");
    }

    return String.from(val.substring(dotIndex + 1, vIndex));
  }

  /**
   * Organisation originating the reference model on which this archetype is based, e.g. \`"openEHR"\`, \`"CEN"\`, \`"HL7"\`.
   * @returns Result value
   */
  rm_originator(): String {
    const qualified = this.qualified_rm_entity().value || "";
    const parts = qualified.split("-");
    if (parts.length < 3) {
      throw new Error("Invalid qualified_rm_entity format");
    }
    return String.from(parts[0]);
  }

  /**
   * Name of the reference model, e.g. \`"RIM"\`,  \`"EHR"\`,  \`"EN13606"\`.
   * @returns Result value
   */
  rm_name(): String {
    const qualified = this.qualified_rm_entity().value || "";
    const parts = qualified.split("-");
    if (parts.length < 3) {
      throw new Error("Invalid qualified_rm_entity format");
    }
    return String.from(parts[1]);
  }

  /**
   * Name of the ontological level within the reference model to which this archetype is targeted, e.g. for openEHR:  \`"FOLDER"\`, \`"COMPOSITION"\`, \`"SECTION"\`, \`"OBSERVATION"\`.
   * @returns Result value
   */
  rm_entity(): String {
    const qualified = this.qualified_rm_entity().value || "";
    const parts = qualified.split("-");
    if (parts.length < 3) {
      throw new Error("Invalid qualified_rm_entity format");
    }
    return String.from(parts[2]);
  }

  /**
   * Name of specialisation of concept, if this archetype is a specialisation of another archetype, e.g. \`"cholesterol"\`.
   * @returns Result value
   */
  specialisation(): String {
    const concept = this.domain_concept().value || "";
    const hyphenIndex = concept.indexOf("-");

    if (hyphenIndex === -1) {
      return String.from("");
    }

    return String.from(concept.substring(hyphenIndex + 1));
  }

  /**
   * Version of this archetype.
   *
   * @returns Result value
   */
  version_id(): String {
    const val = this.value || "";
    const vIndex = val.lastIndexOf(".v");

    if (vIndex === -1) {
      throw new Error("Invalid ARCHETYPE_ID format: no '.v' found");
    }

    // Return everything after '.v'
    return String.from(val.substring(vIndex + 2));
  }
}

/**
 * Generic identifier type for identifiers whose format is otherwise unknown to openEHR. Includes an attribute for naming the identification scheme (which may well be local).
 */
export class GENERIC_ID extends OBJECT_ID {
  /**
   * Internal storage for scheme
   * @protected
   */
  protected _scheme?: String;

  /**
   * Name of the scheme to which this identifier conforms. Ideally this name will be recognisable globally but realistically it may be a local ad hoc scheme whose name is not controlled or standardised in any way.
   */
  get scheme(): string | undefined {
    return this._scheme?.value;
  }

  /**
   * Gets the String wrapper object for scheme.
   * Use this to access String methods.
   */
  get $scheme(): String | undefined {
    return this._scheme;
  }

  /**
   * Sets scheme from either a primitive value or String wrapper.
   */
  set scheme(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._scheme = undefined;
    } else if (typeof val === "string") {
      this._scheme = String.from(val);
    } else {
      this._scheme = val;
    }
  }
}

/**
 * Abstract model of UID-based identifiers consisting of a root part and an optional extension; lexical form: \`root '::' extension\`.
 */
export abstract class UID_BASED_ID extends OBJECT_ID {
  /**
   * The identifier of the conceptual namespace in which the object exists, within the identification scheme. Returns the part to the left of the first '::' separator, if any, or else the whole string.
   * @returns Result value
   */
  root(): UID {
    const val = this.value || "";
    const separatorIndex = val.indexOf("::");
    const rootValue = separatorIndex === -1
      ? val
      : val.substring(0, separatorIndex);

    // Try to determine UID type and create appropriate instance
    // For now, create a generic UUID or ISO_OID based on format
    if (rootValue.includes("-")) {
      // Looks like UUID
      const uuid = new UUID();
      uuid.value = rootValue;
      return uuid;
    } else if (rootValue.match(/^\d+(\.\d+)*$/)) {
      // Looks like ISO OID
      const oid = new ISO_OID();
      oid.value = rootValue;
      return oid;
    } else {
      // Default to INTERNET_ID
      const internetId = new INTERNET_ID();
      internetId.value = rootValue;
      return internetId;
    }
  }

  /**
   * Optional local identifier of the object within the context of the root identifier. Returns the part to the right of the first '::' separator if any, or else any empty String.
   * @returns Result value
   */
  extension(): String {
    const val = this.value || "";
    const separatorIndex = val.indexOf("::");
    if (separatorIndex === -1) {
      return String.from("");
    }
    return String.from(val.substring(separatorIndex + 2));
  }

  /**
   * True if not \`_extension_.is_empty()\`.
   * @returns Result value
   */
  has_extension(): Boolean {
    return new Boolean(!this.extension().is_empty().value);
  }
}

/**
 * Concrete type corresponding to hierarchical identifiers of the form defined by \`UID_BASED_ID\`.
 */
export class HIER_OBJECT_ID extends UID_BASED_ID {
}

/**
 * Class describing a reference to another object, which may exist locally or be maintained outside the current namespace, e.g. in another service. Services are usually external, e.g. available in a LAN (including on the same host) or the internet via Corba, SOAP, or some other distributed protocol. However, in small systems they may be part of the same executable as the data containing the Id.
 */
export class OBJECT_REF {
  /**
   * Internal storage for namespace
   * @protected
   */
  protected _namespace?: String;

  /**
   * Namespace to which this identifier belongs in the local system context (and possibly in any other openEHR compliant environment) e.g.  terminology ,  demographic . These names are not yet standardised. Legal values for \`_namespace_\` are:
   *
   * * \`"local"\`
   * * \`"unknown"\`
   * * a string matching the standard regex \`[a-zA-Z][a-zA-Z0-9_.:\/&?=+-]*\`.
   *
   * Note that the first two are just special values of the regex, and will be matched by it.
   */
  get namespace(): string | undefined {
    return this._namespace?.value;
  }

  /**
   * Gets the String wrapper object for namespace.
   * Use this to access String methods.
   */
  get $namespace(): String | undefined {
    return this._namespace;
  }

  /**
   * Sets namespace from either a primitive value or String wrapper.
   */
  set namespace(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._namespace = undefined;
    } else if (typeof val === "string") {
      this._namespace = String.from(val);
    } else {
      this._namespace = val;
    }
  }

  /**
   * Internal storage for type
   * @protected
   */
  protected _type?: String;

  /**
   * Name of the  class (concrete or abstract) of object to which this identifier type refers, e.g. \`PARTY\`, \`PERSON\`,  \`GUIDELINE\`  etc. These class names are from the relevant reference model. The type name \`ANY\` can be used to indicate that any type is accepted (e.g. if the type is unknown).
   */
  get type(): string | undefined {
    return this._type?.value;
  }

  /**
   * Gets the String wrapper object for type.
   * Use this to access String methods.
   */
  get $type(): String | undefined {
    return this._type;
  }

  /**
   * Sets type from either a primitive value or String wrapper.
   */
  set type(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._type = undefined;
    } else if (typeof val === "string") {
      this._type = String.from(val);
    } else {
      this._type = val;
    }
  }

  /**
   * Globally unique id of an object, regardless of where it is stored.
   */
  id?: OBJECT_ID;

  /**
   * Compare two OBJECT_REF instances for equality.
   * @param other - The other OBJECT_REF to compare with
   * @returns Boolean indicating if they are equal
   */
  is_equal(other: any): Boolean {
    if (!(other instanceof OBJECT_REF)) {
      return new Boolean(false);
    }

    // Compare namespace
    if (this.namespace !== other.namespace) {
      return new Boolean(false);
    }

    // Compare type
    if (this.type !== other.type) {
      return new Boolean(false);
    }

    // Compare id
    if (this.id && other.id) {
      if (this.id instanceof Any && other.id instanceof Any) {
        return this.id.is_equal(other.id);
      }
    } else if (!this.id && !other.id) {
      return new Boolean(true);
    } else {
      return new Boolean(false);
    }

    return new Boolean(true);
  }
}

/**
 * Reference to a \`LOCATABLE\` instance inside the top-level content structure inside a \`VERSION<T>\` identified by the \`_id_\` attribute. The \`_path_\` attribute is applied to the object that \`VERSION._data_\` points to.
 */
export class LOCATABLE_REF extends OBJECT_REF {
  /**
   * Internal storage for path
   * @protected
   */
  protected _path?: String;

  /**
   * The path to an instance, as an absolute path with respect to the object found at \`VERSION._data_\`. An empty path means that the object referred to by \`_id_\` is being specified.
   */
  get path(): string | undefined {
    return this._path?.value;
  }

  /**
   * Gets the String wrapper object for path.
   * Use this to access String methods.
   */
  get $path(): String | undefined {
    return this._path;
  }

  /**
   * Sets path from either a primitive value or String wrapper.
   */
  set path(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._path = undefined;
    } else if (typeof val === "string") {
      this._path = String.from(val);
    } else {
      this._path = val;
    }
  }

  /**
   * Globally unique id of an object, regardless of where it is stored.
   */
  override id?: UID_BASED_ID = undefined;
  /**
   * A URI form of the reference, created by concatenating the following:
   *
   * * scheme, e.g. \`ehr:\`, derived from \`_namespace_\`
   * * \`_id.value_\`
   * * \`/\` + \`_path_\`, where \`_path_\` is non-empty
   *
   * @returns Result value
   */
  as_uri(): String {
    const namespace = this.namespace || "";
    const idValue = this.id?.value || "";
    const path = this.path || "";
    
    let uri = `${namespace}:${idValue}`;
    if (path && path.length > 0) {
      uri += `/${path}`;
    }
    
    return String.from(uri);
  }
}

/**
 * Globally unique identifier for one version of a versioned object; lexical form: \`object_id  '::' creating_system_id  '::' version_tree_id\`.
 */
export class OBJECT_VERSION_ID extends UID_BASED_ID {
  /**
   * Unique identifier for logical object of which this identifier identifies one version; normally the \`_object_id_\` will be the unique identifier of the version container containing the version referred to by this \`OBJECT_VERSION_ID\` instance.
   * @returns Result value
   */
  object_id(): UID {
    const val = this.value || "";
    const parts = val.split("::");
    if (parts.length !== 3) {
      throw new Error("OBJECT_VERSION_ID must have 3 parts separated by '::'");
    }
    // Create a UUID from the first part
    const uid = new UUID();
    uid.value = parts[0];
    return uid;
  }

  /**
   * Identifier of the system that created the Version corresponding to this Object version id.
   * @returns Result value
   */
  creating_system_id(): UID {
    const val = this.value || "";
    const parts = val.split("::");
    if (parts.length !== 3) {
      throw new Error("OBJECT_VERSION_ID must have 3 parts separated by '::'");
    }
    // Create an INTERNET_ID from the second part
    const uid = new INTERNET_ID();
    uid.value = parts[1];
    return uid;
  }

  /**
   * Tree identifier of this version with respect to other versions in the same version tree, as either 1 or 3 part dot-separated numbers, e.g.  1 ,  2.1.4 .
   * @returns Result value
   */
  version_tree_id(): VERSION_TREE_ID {
    const val = this.value || "";
    const parts = val.split("::");
    if (parts.length !== 3) {
      throw new Error("OBJECT_VERSION_ID must have 3 parts separated by '::'");
    }
    const versionTreeId = new VERSION_TREE_ID();
    versionTreeId.value = parts[2];
    return versionTreeId;
  }

  /**
   * True if this version identifier represents a branch.
   * @returns Result value
   */
  is_branch(): Boolean {
    return this.version_tree_id().is_branch();
  }
}

/**
 * Identifier for parties in a demographic or identity service. There are typically a number of subtypes of the \`PARTY\` class, including \`PERSON\`, \`ORGANISATION\`, etc. Abstract supertypes are allowed if the referenced object is of a type not known by the current implementation of this class (in other words, if the demographic model is changed by the addition of a new \`PARTY\` or \`ACTOR\` subtypes, valid \`PARTY_REFs\` can still be constructed to them).
 */
export class PARTY_REF extends OBJECT_REF {
}

/**
 * Identifier for terminologies such as accessed via a terminology query service. In this class, the value attribute identifies the Terminology in the terminology service, e.g.  SNOMED-CT . A terminology is assumed to be in a particular language, which must be explicitly specified.
 *
 * Lexical form: \`name [  '(' version  ')' ]\`.
 */
export class TERMINOLOGY_ID extends OBJECT_ID {
  /**
   * Return the terminology id (which includes the  version  in some cases). Distinct names correspond to distinct (i.e. non-compatible) terminologies. Thus the names  \`"ICD10AM"\` and \`"ICD10"\` refer to distinct terminologies.
   * @returns Result value
   */
  name(): String {
    const val = this.value || "";
    // Format: name[(version)]
    const parenIndex = val.indexOf("(");
    if (parenIndex > 0) {
      return String.from(val.substring(0, parenIndex));
    }
    return String.from(val);
  }

  /**
   * Version of this terminology, if versioning supported, else the empty string.
   * @returns Result value
   */
  version_id(): String {
    const val = this.value || "";
    // Format: name[(version)]
    const parenIndex = val.indexOf("(");
    if (parenIndex > 0) {
      const closeParenIndex = val.indexOf(")");
      if (closeParenIndex > parenIndex) {
        return String.from(val.substring(parenIndex + 1, closeParenIndex));
      }
    }
    return String.from("");
  }
}

/**
 * Version tree identifier for one version. Lexical form:
 *
 * \`trunk_version [  '.' branch_number  '.' branch_version ]\`
 */
export class VERSION_TREE_ID {
  /**
   * Internal storage for value
   * @protected
   */
  protected _value?: String;

  /**
   * String form of this identifier.
   */
  get value(): string | undefined {
    return this._value?.value;
  }

  /**
   * Gets the String wrapper object for value.
   * Use this to access String methods.
   */
  get $value(): String | undefined {
    return this._value;
  }

  /**
   * Sets value from either a primitive value or String wrapper.
   */
  set value(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._value = undefined;
    } else if (typeof val === "string") {
      this._value = String.from(val);
    } else {
      this._value = val;
    }
  }

  /**
   * Trunk version number; numbering starts at 1.
   * @returns Result value
   */
  trunk_version(): String {
    const val = this.value || "";
    const parts = val.split(".");
    return String.from(parts[0]);
  }

  /**
   * True if this version identifier represents a branch, i.e. has \`_branch_number()_\` and \`_branch_version()_\` parts.
   * @returns Result value
   */
  is_branch(): Boolean {
    const val = this.value || "";
    const parts = val.split(".");
    return new Boolean(parts.length === 3);
  }

  /**
   * Number of branch from the trunk point; numbering starts at 1.
   * @returns Result value
   */
  branch_number(): String {
    const val = this.value || "";
    const parts = val.split(".");
    if (parts.length === 3) {
      return String.from(parts[1]);
    }
    throw new Error("Not a branch version");
  }

  /**
   * Version of the branch; numbering starts at 1.
   * @returns Result value
   */
  branch_version(): String {
    const val = this.value || "";
    const parts = val.split(".");
    if (parts.length === 3) {
      return String.from(parts[2]);
    }
    throw new Error("Not a branch version");
  }
}

/**
 * Abstract parent of classes representing unique identifiers which identify information entities in a durable way. UIDs only ever identify one IE in time or space and are never re-used.
 */
export abstract class UID {
  /**
   * Internal storage for value
   * @protected
   */
  protected _value?: String;

  /**
   * The value of the id.
   */
  get value(): string | undefined {
    return this._value?.value;
  }

  /**
   * Gets the String wrapper object for value.
   * Use this to access String methods.
   */
  get $value(): String | undefined {
    return this._value;
  }

  /**
   * Sets value from either a primitive value or String wrapper.
   */
  set value(val: string | String | undefined) {
    if (val === undefined || val === null) {
      this._value = undefined;
    } else if (typeof val === "string") {
      this._value = String.from(val);
    } else {
      this._value = val;
    }
  }
}

/**
 * Model of the DCE Universal Unique Identifier or UUID which takes the form of hexadecimal integers separated by hyphens, following the pattern 8-4-4-4-12 as defined by the Open Group, CDE 1.1 Remote Procedure Call specification, Appendix A. Also known as a GUID.
 */
export class UUID extends UID {
}

/**
 * Model of a reverse internet domain, as used to uniquely identify an internet domain. In the form of a dot-separated string in the reverse order of a domain name, specified by https://www.rfc-editor.org/info/rfc1034[IETF RFC 1034^].
 */
export class INTERNET_ID extends UID {
}

/**
 * Model of ISO's Object Identifier (oid) as defined by the standard ISO/IEC 8824. Oids are formed from integers separated by dots. Each non-leaf node in an Oid starting from the left corresponds to an assigning authority, and identifies that authority's namespace, inside which the remaining part of the identifier is locally unique.
 */
export class ISO_OID extends UID {
}

/**
 * Identifier for templates. Lexical form to be determined.
 */
export class TEMPLATE_ID extends OBJECT_ID {
}

/**
 * Reference to access group in an access control service.
 */
export class ACCESS_GROUP_REF extends OBJECT_REF {
}
